import { Injectable } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { RelationshipsActions } from '@next/core-lib/relationships';
import { RouterSelectors } from '@next/core-lib/router-store';
import { UserActions } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import * as fecalScoringActions from 'src/app/fecal-scoring/store/actions/fecal-scoring.actions';
import * as feedAdviceActions from 'src/app/feed-advice/store/feed-advice.actions';
import * as productActions from 'src/app/product/store/actions/product.actions';
import * as settingActions from 'src/app/settings/store/actions/settings.actions';

@Injectable()
export class PigEffects {
  constructor(
    private actions$: Actions,
    private readonly store: Store<RootState>,
  ) {}

  customerChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged),
      withLatestFrom(this.store.select(RouterSelectors.getPath)),
      mergeMap(([_, path]) => {
        const parts = path.split('/');
        if (parts.length > 1) {
          return this.getLoadDataActions(parts[1]);
        }
        return of(feedAdviceActions.loadData());
      }),
    ),
  );

  locationInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationInitialized),
      withLatestFrom(this.store.select(RouterSelectors.getPath)),
      mergeMap(([_, path]) => {
        const parts = path.split('/');
        if (parts.length > 1) {
          return this.getLoadDataActions(parts[1]);
        }
        return of(feedAdviceActions.loadData());
      }),
    ),
  );

  relationshipSelectedRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RelationshipsActions.setRelationshipSelectedRedirect),
      switchMap(({ redirectTo }) => {
        const parts = redirectTo ? redirectTo.split('/').slice(1) : [];
        if (parts.length > 1) {
          return of(
            RelationshipsActions.setRelationshipSelectedRedirect({ redirectTo: `/${parts[0]}` }),
          );
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  locationChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationChanged),
      withLatestFrom(this.store.select(RouterSelectors.getPath)),
      mergeMap(([_, path]) => {
        const parts = path.split('/');
        if (parts.length > 1) {
          return this.getLoadDataActions(parts[1]);
        }
        return of(feedAdviceActions.loadData());
      }),
    ),
  );

  getLoadDataActions(urlPart: string): Observable<Action<string>> {
    if (urlPart.indexOf('?') !== -1) {
      urlPart = urlPart.split('?')[0];
    }
    switch (urlPart) {
      case 'feed-advice':
        return of(
          settingActions.loadEnergySetting(),
          settingActions.loadLysineSetting(),
          settingActions.loadDosatorSettingsRequest(),
          feedAdviceActions.loadFeedAdvicesRequest(),
        );
      case 'sows-feed-advice':
        return of(
          settingActions.loadEnergySetting(),
          settingActions.loadLysineSetting(),
          settingActions.loadDosatorSettingsRequest(),
          feedAdviceActions.loadFeedAdvicesRequest(),
        );
      case 'reference-feed-advice':
        return of(
          settingActions.loadEnergySetting(),
          settingActions.loadLysineSetting(),
          feedAdviceActions.loadReferenceFeedAdvicesRequest(),
        );
      case 'product':
        return of(productActions.loadData());
      case 'fecal-scoring':
        return of(fecalScoringActions.loadData());
    }
    return of(feedAdviceActions.loadData());
  }
}
